import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { HelpCircle, ChevronDown, Loader2, ExternalLink, Building2, AlertCircle, FileText, Phone } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Link utili — placeholder. L'utente fornirà i veri link successivamente.
const UTILITY_LINKS = [
  {
    icon: Building2,
    label: "Comune di San Giuliano Terme",
    href: "https://www.comune.sangiulianoterme.pi.it/",
    desc: "Sito ufficiale del Comune",
  },
  {
    icon: Phone,
    label: "Numero unico emergenze",
    href: "tel:112",
    desc: "112",
  },
  {
    icon: AlertCircle,
    label: "Antincendio Toscana 1515",
    href: "tel:1515",
    desc: "1515 — Corpo forestale",
  },
  {
    icon: FileText,
    label: "Statuto dell'Associazione",
    href: "/trasparenza",
    desc: "Documento completo",
  },
];

export default function FaqPage() {
  const [faqs, setFaqs] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/faq`).then((r) => r.json()).then(setFaqs).catch(() => setFaqs([]));
  }, []);

  return (
    <Layout>
      <section className="bg-[#1f2d22] text-white py-16 sm:py-20" data-testid="faq-page-header">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-1.5 mb-5">
            <HelpCircle className="w-3.5 h-3.5 text-[#E8C9A0]" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-[#E8C9A0]">
              FAQ
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            Domande <span className="italic font-light text-[#E8C9A0]">frequenti.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed">
            Risposte rapide e chiare sulle nostre attività, sul volontariato e sull'associazione.
          </p>
        </div>
      </section>

      {/* FAQ accordion */}
      <section className="py-16 sm:py-20 bg-[#FBF9F6]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          {faqs === null ? (
            <div className="flex items-center justify-center py-16 text-[#5C5C5C]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Caricamento…
            </div>
          ) : faqs.length === 0 ? (
            <div className="text-center py-12 text-[#5C5C5C] italic">Nessuna domanda disponibile.</div>
          ) : (
            <div className="divide-y divide-[#E5E1D8] border-y-2 border-[#1A1A1A]/15">
              {faqs.map((f) => {
                const isOpen = open === f.id;
                return (
                  <button
                    key={f.id}
                    type="button"
                    data-testid={`faq-item-${f.id}`}
                    onClick={() => setOpen(isOpen ? null : f.id)}
                    className="w-full text-left py-5 sm:py-6 group"
                  >
                    <div className="flex items-start gap-4">
                      <span className={`mt-1 inline-flex shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-colors ${isOpen ? "border-[#2E4F3B] bg-[#2E4F3B] text-white" : "border-[#1A1A1A]/30 text-[#1A1A1A]"}`}>
                        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </span>
                      <div className="flex-1">
                        <h3 className={`font-display text-base sm:text-lg lg:text-xl font-bold leading-snug ${isOpen ? "text-[#2E4F3B]" : "text-[#1A1A1A] group-hover:text-[#2E4F3B]"} transition-colors`}>
                          {f.question}
                        </h3>
                        {isOpen && (
                          <p className="mt-3 text-sm sm:text-base text-[#5C5C5C] leading-relaxed whitespace-pre-line">
                            {f.answer}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Link utili */}
      <section className="py-16 sm:py-20 bg-white border-t border-[#E5E1D8]" data-testid="faq-utility-section">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="w-10 h-px bg-[#C15C3D]" />
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-[#C15C3D]">
                Link utili
              </span>
              <span className="w-10 h-px bg-[#C15C3D]" />
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-black text-[#1A1A1A] leading-tight tracking-tight">
              Contatti e risorse <span className="italic font-light text-[#2E4F3B]">utili.</span>
            </h2>
            <p className="mt-4 text-sm sm:text-base text-[#5C5C5C] max-w-xl mx-auto">
              Raccolta di link e riferimenti utili. I link verranno aggiornati progressivamente dall'associazione.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
            {UTILITY_LINKS.map((u, i) => {
              const Icon = u.icon;
              const external = u.href.startsWith("http");
              const internal = u.href.startsWith("/");
              const Wrapper = internal ? Link : "a";
              const wrapperProps = internal
                ? { to: u.href }
                : { href: u.href, target: external ? "_blank" : undefined, rel: external ? "noopener noreferrer" : undefined };
              return (
                <Wrapper
                  key={i}
                  {...wrapperProps}
                  data-testid={`utility-link-${i}`}
                  className="group flex items-center gap-4 p-5 rounded-2xl bg-[#FBF9F6] border-2 border-[#E5E1D8] hover:border-[#2E4F3B] transition-all hover:-translate-y-0.5"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#2E4F3B] text-white flex items-center justify-center shrink-0 group-hover:bg-[#1f3828] transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-base sm:text-lg text-[#1A1A1A] leading-tight">{u.label}</div>
                    <div className="text-xs sm:text-sm text-[#5C5C5C] mt-1">{u.desc}</div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-[#5C5C5C] shrink-0 group-hover:text-[#2E4F3B]" />
                </Wrapper>
              );
            })}
          </div>

          <p className="mt-8 text-center text-xs text-[#5C5C5C] italic">
            Altri link e contatti utili saranno aggiunti nel tempo.
          </p>
        </div>
      </section>
    </Layout>
  );
}
